/**
 * panelPlacement – remembering where a viewer put a UI surface, and the ray maths
 * for moving and resizing it.
 *
 * The lesson panel has always drawn a grab bar and a resize corner
 * (drawLessonPanel's `panelGrab` / `panelResize` regions), and V3 has always
 * ignored them, so a panel that landed in front of a model or out of a student's
 * comfortable view stayed there. This file is the half of the fix that does not
 * touch the scene graph: what a placement IS, how it is stored, and how a
 * pointer's ray turns into a new position or size.
 *
 * A placement is stored RELATIVE TO THE ANCHOR, never as world coordinates. The
 * anchor is recaptured when a headset session starts and on every recentre, and
 * the whole workspace moves with it (workspace.ts). A remembered world position
 * would stay behind while the desk and the models moved, and the next session
 * would open with the panel somewhere behind the student. As a bearing, a
 * distance and a height against the anchor, the panel moves with the rest of the
 * room and keeps the arrangement the student chose.
 *
 *   - The bearing uses workspace.ts's own convention and helpers (degrees,
 *     negative to the left), so a stored bearing and a frame bearing mean the
 *     same thing.
 *   - The height is against the EYE LINE (groundY + eyeHeight), the datum the
 *     frame places the panel from, so a panel set up standing is still level
 *     with the eyes once the student sits down.
 *   - The scale multiplies the frame's size. The frame still decides the
 *     geometry; the student's resize lives only in the mesh's scale.
 *
 * Kept separately per surface and per mode (`learnxr_v3_ui:panel:xr`,
 * `learnxr_v3_ui:panel:flat`): where a panel fits in a headset and where it fits
 * on a laptop have nothing to do with each other, and one overwriting the other
 * would leave both wrong.
 *
 * Move and resize are ported from krpano's panel drag
 * (public/krpano/plugins/immersive_ui.xml, `immersive_ui_install_panel_drag`).
 * There, krpano's own drag3d carried a handle along the pointer at a fixed
 * distance and the panel followed the handle; a resize scaled by how much further
 * the corner handle had been pulled from the panel's centre. three.js has no
 * drag3d, so the same two rules are written out here as ray maths.
 *
 * Deliberately pure: storage is passed in rather than touched, and nothing here
 * adds to or reads from a scene, so it all runs under node:test.
 */

import * as THREE from 'three';

import { bearing, bearingTowards, yawTowards } from './workspace';
import type { WorkspaceAnchor } from './workspace';

/** The surfaces a viewer can place. `image` is the VR image plane. */
export type UiSurface = 'panel' | 'image';

/** A headset and a flat screen remember their placements separately. */
export type ViewMode = 'xr' | 'flat';

/**
 * Nearest a surface may be put. Closer than this it is over the desk, among the
 * models a controller is trying to reach, and text that close is harder to focus
 * on, not easier.
 */
export const MIN_SURFACE_DISTANCE = 0.9;

/**
 * Furthest a surface may be put. The panel's size is fixed in metres, so at 4m it
 * subtends half the angle it does at the default 2m — about the smallest its text
 * stays readable at.
 */
export const MAX_SURFACE_DISTANCE = 4;

/**
 * Lowest a surface may sit, below the eye line. About desk height for a standing
 * adult: lower than that the student is reading their lap.
 */
export const MIN_SURFACE_HEIGHT_OFFSET = -0.9;

/**
 * Highest a surface may sit, above the eye line. At the default distance that is
 * a gaze tilted up by about 17 degrees, the most a neck holds comfortably over a
 * lesson.
 */
export const MAX_SURFACE_HEIGHT_OFFSET = 0.6;

/**
 * Smallest resize. Half size is already a small panel at 2m; anything smaller only
 * makes text a student cannot read.
 */
export const MIN_SURFACE_SCALE = 0.5;

/** Largest resize, the same upper limit krpano's panel uses. */
export const MAX_SURFACE_SCALE = 2.5;

/** Where a viewer put a surface, relative to the workspace anchor. */
export interface SurfacePref {
  /** Format version, so a future shape is rejected rather than misread. */
  v: 1;
  /** Bearing of the surface's centre from the anchor, in degrees, negative to the left. */
  bearingDeg: number;
  /** Horizontal distance from the anchor to the surface's centre, in metres. */
  distance: number;
  /** Height of the surface's centre above the viewer's eye line, in metres. */
  heightOffset: number;
  /** Uniform scale on top of the size the frame gives the surface. */
  scale: number;
}

/** The storage calls this file uses. `window.localStorage` satisfies it. */
export type PrefStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** Y of the viewer's eyes: the datum heights are stored against. */
function eyeLine(anchor: WorkspaceAnchor): number {
  return anchor.groundY + anchor.eyeHeight;
}

/** Degrees into (-180, 180], so a bearing has exactly one spelling. */
function wrapDegrees(degrees: number): number {
  const wrapped = ((degrees % 360) + 360) % 360;
  return wrapped > 180 ? wrapped - 360 : wrapped;
}

/** A preference with every field inside its limits. */
function withinLimits(
  bearingDeg: number,
  distance: number,
  heightOffset: number,
  scale: number
): SurfacePref {
  return {
    v: 1,
    bearingDeg: wrapDegrees(bearingDeg),
    distance: THREE.MathUtils.clamp(distance, MIN_SURFACE_DISTANCE, MAX_SURFACE_DISTANCE),
    heightOffset: THREE.MathUtils.clamp(
      heightOffset,
      MIN_SURFACE_HEIGHT_OFFSET,
      MAX_SURFACE_HEIGHT_OFFSET
    ),
    scale: THREE.MathUtils.clamp(scale, MIN_SURFACE_SCALE, MAX_SURFACE_SCALE),
  };
}

/**
 * The preference for a surface centred at `position` with uniform `scale`.
 *
 * Clamped, so what is saved is always something `poseFromPref` can place
 * sensibly. A surface dragged beyond the limits comes back at the limit; apply
 * `poseFromPref` to the result on release and the viewer sees where it will be
 * next time, rather than being surprised by it after a reload.
 */
export function prefFromPose(
  anchor: WorkspaceAnchor,
  position: THREE.Vector3,
  scale: number
): SurfacePref {
  const distance = Math.hypot(position.x - anchor.position.x, position.z - anchor.position.z);
  return withinLimits(
    bearingTowards(anchor.forward, anchor.position, position),
    distance,
    position.y - eyeLine(anchor),
    scale
  );
}

/**
 * Where a preference puts the surface for this anchor.
 *
 * `yaw` faces the anchor, as the frame's own panel yaw does; V3 then keeps it
 * turned to the viewer every frame.
 */
export function poseFromPref(
  anchor: WorkspaceAnchor,
  pref: SurfacePref
): { position: THREE.Vector3; yaw: number; scale: number } {
  const position = anchor.position
    .clone()
    .addScaledVector(bearing(anchor.forward, pref.bearingDeg), pref.distance);
  position.y = eyeLine(anchor) + pref.heightOffset;
  return { position, yaw: yawTowards(position, anchor.position), scale: pref.scale };
}

/**
 * A stored value as a preference, or null when it is not one.
 *
 * Whatever is in localStorage may be from an older build, hand-edited, or
 * truncated, so this accepts only the exact shape — `v: 1` and four finite
 * numbers — and returns null for anything else rather than placing a panel at
 * NaN. Numbers that are merely out of range are clamped, not rejected: a panel
 * saved at 5m is a panel the student wanted far away, and 4m honours that better
 * than the default does.
 */
export function clampPref(raw: unknown): SurfacePref | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const { v, bearingDeg, distance, heightOffset, scale } = raw as Record<string, unknown>;
  if (v !== 1) return null;
  const fields = [bearingDeg, distance, heightOffset, scale];
  if (!fields.every((n) => typeof n === 'number' && Number.isFinite(n))) return null;
  return withinLimits(
    bearingDeg as number,
    distance as number,
    heightOffset as number,
    scale as number
  );
}

/** The localStorage key for one surface in one mode. */
export function prefKey(surface: UiSurface, mode: ViewMode): string {
  return `learnxr_v3_ui:${surface}:${mode}`;
}

/**
 * The stored preference, or null for "use the frame's default".
 *
 * Never throws. Storage can be missing, blocked (private browsing) or throw on
 * access, and none of those should cost the student their panel — they only
 * cost them the remembered placement.
 */
export function loadPref(
  storage: PrefStorage | null | undefined,
  surface: UiSurface,
  mode: ViewMode
): SurfacePref | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(prefKey(surface, mode));
    return raw ? clampPref(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

/**
 * Store a preference, or clear it with `null` (the reset gesture).
 *
 * Never throws, for the same reason as `loadPref`: a full or blocked storage
 * means the placement is not remembered, and nothing worse.
 */
export function savePref(
  storage: PrefStorage | null | undefined,
  surface: UiSurface,
  mode: ViewMode,
  pref: SurfacePref | null
): void {
  if (!storage) return;
  const key = prefKey(surface, mode);
  try {
    if (pref) storage.setItem(key, JSON.stringify(pref));
    else storage.removeItem(key);
  } catch {
    // Quota exceeded or storage blocked: keep the session's placement, lose only
    // the memory of it.
  }
}

/** A move in progress. */
export interface MoveDrag {
  /** How far along the pointer's ray the grab point was. Held for the whole drag. */
  distance: number;
  /** Surface centre minus grab point. Held for the whole drag. */
  offset: THREE.Vector3;
}

/**
 * Start moving a surface that the pointer's `ray` hit at `hitPoint`.
 *
 * Remembers two things: how far along the ray the grab point was, and where the
 * surface's centre was relative to it. The grab point then rides the ray at that
 * distance and the surface keeps the same offset from it — krpano's drag3d, where
 * the handle rode the pointer and the panel followed the handle. Keeping the
 * offset is what stops the panel jumping to centre itself on the pointer the
 * moment it is grabbed by its bar.
 */
export function beginMove(
  ray: THREE.Ray,
  hitPoint: THREE.Vector3,
  surfacePos: THREE.Vector3
): MoveDrag {
  return {
    distance: ray.origin.distanceTo(hitPoint),
    offset: surfacePos.clone().sub(hitPoint),
  };
}

/**
 * Where the surface's centre goes for the pointer's current `ray`.
 *
 * `ray.direction` must be unit length, as a Raycaster's always is: the grab
 * distance is measured along it.
 */
export function updateMove(drag: MoveDrag, ray: THREE.Ray): THREE.Vector3 {
  return ray.at(drag.distance, new THREE.Vector3()).add(drag.offset);
}

/** A resize in progress. */
export interface ResizeDrag {
  /** The surface's plane when the resize began. Held for the whole drag. */
  plane: THREE.Plane;
  /** The surface's centre, which a resize does not move. */
  center: THREE.Vector3;
  /** How far from the centre, in the plane, the corner was grabbed. */
  reach0: number;
  /** The surface's scale when the resize began. */
  scale0: number;
}

/**
 * Start resizing a surface centred at `surfacePos`, facing `normal` (world space),
 * whose resize corner was hit at `hitPoint`, at scale `scale0`.
 */
export function beginResize(
  surfacePos: THREE.Vector3,
  normal: THREE.Vector3,
  hitPoint: THREE.Vector3,
  scale0: number
): ResizeDrag {
  const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(
    normal.clone().normalize(),
    surfacePos
  );
  const grabbed = plane.projectPoint(hitPoint, new THREE.Vector3());
  return { plane, center: surfacePos.clone(), reach0: grabbed.distanceTo(surfacePos), scale0 };
}

/**
 * The new scale for the pointer's current `ray`, or null when there is none.
 *
 * The rule is krpano's: scale by how much further from the centre the corner has
 * been pulled, `scale0 * reach / reach0`, clamped to the scale limits. Because the
 * corner's distance from the centre grows exactly with the scale, the corner then
 * stays under the pointer as it moves. The pointer is followed where its ray
 * crosses the surface's plane, which is the in-plane distance krpano measured.
 *
 * Null when the ray runs parallel to the plane or points away from it, or when
 * the grab was so close to the centre that no ratio means anything. The caller
 * keeps the current scale for that frame.
 */
export function updateResize(drag: ResizeDrag, ray: THREE.Ray): number | null {
  if (drag.reach0 < 1e-4) return null;
  const hit = ray.intersectPlane(drag.plane, new THREE.Vector3());
  if (!hit) return null;
  return THREE.MathUtils.clamp(
    drag.scale0 * (hit.distanceTo(drag.center) / drag.reach0),
    MIN_SURFACE_SCALE,
    MAX_SURFACE_SCALE
  );
}
